import _ from 'lodash'
import type {
    Pointer,
    DependenciesManager,
    Namespace,
    ComponentFeatures,
    ComponentFeature,
    PredefinedComponentFeaturesGroups,
    DependenciesBuilder
} from '@wix/document-services-types'
import {
    ADDITIONAL_SAVED_NAMESPACES,
    BASED_ON_SIGNATURE_NS,
    COMP_DATA_QUERY_KEYS_WITH_STYLE,
    DATA_TYPES,
    PARTIALLY_SAVED_NAMESPACES,
    VIEW_MODES,
    EDITOR_DATA_TYPES
} from '../../../constants/constants'
import type {DalValue} from '@wix/document-manager-core'
import {
    ExtendedRefInfo,
    UnmarkedRefInfo,
    getComponentFeatures,
    unmarkedReferences,
    type DetailedValue
} from '@wix/import-export-utils'
// eslint-disable-next-line wix-editor/no-internal-import
import * as referableByExternalNodesJson from '@wix/document-services-json-schemas/dist/referableByExternalNodes.json'

export interface ExternalsHandler<T = DalValue> {
    pack(ref: DetailedValue<T>, dependenciesBuilder: DependenciesBuilder, refPointer: Pointer, item: DalValue): void
    unpack(
        ref: DetailedValue<T>,
        dependenciesManager: DependenciesManager,
        refFieldInfo: ExtendedRefInfo,
        item: DalValue
    ): void
}

export type DataNodesWhitelist = Set<Pointer['id']> | 'All'

interface GlobalNamespaces {
    [namespace: Namespace]: DataNodesWhitelist
}

export const COMPONENT_FEATURES: ComponentFeatures = getComponentFeatures(COMP_DATA_QUERY_KEYS_WITH_STYLE)

export const EXCLUDED_NAMESPACES: Set<Namespace> = new Set([
    DATA_TYPES.mobileHints,
    DATA_TYPES.atomicScopes,
    VIEW_MODES.MOBILE,
    BASED_ON_SIGNATURE_NS
])

const GLOBAL_DATA_EXCLUSIONS: string[] = ['appsState']

export const GLOBAL_NAMESPACES: GlobalNamespaces = {
    ...Object.fromEntries(
        ADDITIONAL_SAVED_NAMESPACES.filter(namespace => !GLOBAL_DATA_EXCLUSIONS.includes(namespace)).map(namespace => [
            namespace,
            'All'
        ])
    ),
    ..._.mapValues(PARTIALLY_SAVED_NAMESPACES, dataNodes => new Set(dataNodes))
}

const EXCLUDED_FEATURES: Set<Namespace> = new Set([DATA_TYPES.fixerVersions])

const ALL_FEATURES = _.omitBy(
    COMPONENT_FEATURES,
    ({namespace}: ComponentFeature) => EXCLUDED_NAMESPACES.has(namespace) || EXCLUDED_FEATURES.has(namespace)
)

const EML_DATA_TYPES_SET: Set<Namespace> = new Set([DATA_TYPES.data, DATA_TYPES.theme, DATA_TYPES.layout])
const EDITOR_DATA_TYPES_SET: Set<Namespace> = new Set(Object.values(EDITOR_DATA_TYPES))

export const PREDEFINED_COMPONENT_FEATURES_GROUPS: PredefinedComponentFeaturesGroups = {
    All: ALL_FEATURES,
    PublicViewerData: _.omitBy(ALL_FEATURES, ({namespace}: ComponentFeature) => EDITOR_DATA_TYPES_SET.has(namespace)),
    EMLData: _.pickBy(ALL_FEATURES, ({namespace}: ComponentFeature) => EML_DATA_TYPES_SET.has(namespace))
}

export const COMPONENT_QUERY_TO_NAMESPACE_MAP = _(COMPONENT_FEATURES)
    .keyBy('query')
    .mapValues(({namespace}: ComponentFeature) => namespace)
    .value()

export const COMPONENT_QUERIES = new Set(Object.keys(COMPONENT_QUERY_TO_NAMESPACE_MAP))

export const UNNECESSARY_PROPS = ['parent', 'metaData', 'mobileMetaData', 'modes', 'componentProperties', 'themeData']
export const BASE_STRUCTURE_PROPERTIES = ['layout']
export const STRUCTURE_PROPERTIES_TO_KEEP = new Set([...UNNECESSARY_PROPS, 'components', 'id'])

export const DEFAULT_BREAKPOINTS_MAX_RANGE = 2 ** 31 - 1

const unmarkedReferrableTypes: Record<Namespace, Record<string, boolean>> = _.mapValues(
    unmarkedReferences,
    (items: Record<string, UnmarkedRefInfo[]>) => {
        const referredTypes = {}
        _.forOwn(items, (references: UnmarkedRefInfo[]) => {
            for (const {refTypes} of references) {
                _.forEach(refTypes, (refType: string) => {
                    referredTypes[refType] = true
                })
            }
        })
        return referredTypes
    }
)

export const REFERABLE_BY_EXTERNAL_NODES = _.merge(referableByExternalNodesJson, unmarkedReferrableTypes)

export const NON_EXISTING_COMPONENT_ERROR_TYPE = 'IMPORT_EXPORT_NON_EXISTING_COMPONENT'
